import { getUserData } from './authStorage';

const getOrgConfig = () => {
  const userData = getUserData();
  return userData?.organization_unit?.config || {};
};

const getModuleConfig = () => {
  return getOrgConfig()?.module_config || {};
};

// ─── Currency ────────────────────────────────────────────────────────────────

export const getCurrencySymbol = () => {
  return getModuleConfig()?.currency_symbol || '$';
};

export const getCurrencyCode = () => {
  return getModuleConfig()?.currency || 'USD';
};

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return `${getCurrencySymbol()}0.00`;
  const locale = getLocale();
  const currency = getCurrencyCode();
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(parseFloat(amount));
  } catch {
    return `${getCurrencySymbol()}${parseFloat(amount).toFixed(2)}`;
  }
};

// ─── Locale & Timezone ───────────────────────────────────────────────────────

export const getLocale = () => {
  return getModuleConfig()?.locale || getOrgConfig()?.locale || 'en-US';
};

export const getTimezone = () => {
  return getModuleConfig()?.timezone || getOrgConfig()?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// ─── Date Formats (from org config) ─────────────────────────────────────────
// date_short      → "dd/MMM/yy"
// date_long       → "dd/MMM/yy h:mm a"
// date_short_numeric → "dd/MM/yy"
// date_long_numeric  → "dd/MM/yy h:mm a"

const TOKEN_MAP = {
  'dd':   (d) => String(d.getDate()).padStart(2, '0'),
  'd':    (d) => String(d.getDate()),
  'MMM':  (d) => d.toLocaleString('en-US', { month: 'short' }),
  'MM':   (d) => String(d.getMonth() + 1).padStart(2, '0'),
  'M':    (d) => String(d.getMonth() + 1),
  'yyyy': (d) => String(d.getFullYear()),
  'yy':   (d) => String(d.getFullYear()).slice(-2),
  'HH':   (d) => String(d.getHours()).padStart(2, '0'),
  'H':    (d) => String(d.getHours()),
  'hh':   (d) => String(d.getHours() % 12 || 12).padStart(2, '0'),
  'h':    (d) => String(d.getHours() % 12 || 12),
  'mm':   (d) => String(d.getMinutes()).padStart(2, '0'),
  'ss':   (d) => String(d.getSeconds()).padStart(2, '0'),
  'a':    (d) => d.getHours() < 12 ? 'AM' : 'PM',
};

const applyFormat = (date, pattern) => {
  return pattern.replace(
    /yyyy|yy|MMM|MM|M|dd|d|HH|H|hh|h|mm|ss|a/g,
    (token) => TOKEN_MAP[token]?.(date) ?? token
  );
};

const toLocalDate = (value) => {
  if (!value) return null;
  let str = String(value);
  if (!str.includes('T') && !str.includes('Z') && !str.includes('+')) {
    str = str.replace(' ', 'T') + 'Z';
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const toZonedDate = (utcDate) => {
  const tz = getTimezone();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(utcDate);
  const p = {};
  parts.forEach(({ type, value }) => { p[type] = value; });
  return new Date(`${p.year}-${p.month}-${p.day}T${p.hour === '24' ? '00' : p.hour}:${p.minute}:${p.second}`);
};

// Format date using org's date_short pattern (date only)
export const formatDate = (value) => {
  const date = toLocalDate(value);
  if (!date) return 'N/A';
  const zoned = toZonedDate(date);
  const pattern = getOrgConfig()?.date_short || 'dd/MMM/yy';
  return applyFormat(zoned, pattern);
};

// Format date+time using org's date_long pattern
export const formatDateTime = (value) => {
  const date = toLocalDate(value);
  if (!date) return 'N/A';
  const zoned = toZonedDate(date);
  const isMidnight = zoned.getHours() === 0 && zoned.getMinutes() === 0 && zoned.getSeconds() === 0;
  const pattern = isMidnight
    ? (getOrgConfig()?.date_short || 'dd/MMM/yy')
    : (getOrgConfig()?.date_long || 'dd/MMM/yy h:mm a');
  return applyFormat(zoned, pattern);
};

// Format using org's numeric short date pattern
export const formatDateNumeric = (value) => {
  const date = toLocalDate(value);
  if (!date) return 'N/A';
  const zoned = toZonedDate(date);
  const pattern = getOrgConfig()?.date_short_numeric || 'dd/MM/yy';
  return applyFormat(zoned, pattern);
};

// Format using org's numeric long date+time pattern
export const formatDateTimeNumeric = (value) => {
  const date = toLocalDate(value);
  if (!date) return 'N/A';
  const zoned = toZonedDate(date);
  const isMidnight = zoned.getHours() === 0 && zoned.getMinutes() === 0 && zoned.getSeconds() === 0;
  const pattern = isMidnight
    ? (getOrgConfig()?.date_short_numeric || 'dd/MM/yy')
    : (getOrgConfig()?.date_long_numeric || 'dd/MM/yy h:mm a');
  return applyFormat(zoned, pattern);
};
